import type { CanonicalEntry } from "./canonical-entry.js";
import { GlossMemory } from "./gloss-memory.js";
import { askedSite, glossUrl } from "./gloss-request.js";
import { GlossStore } from "./gloss-store.js";

/**
 * What the site says about the words on a page, asked one word at a time.
 *
 * Three places an answer can come from, in this order. The caller's own
 * memory, which lasts as long as its worker or its page does and is
 * `gloss-memory.ts`. The store on the device, which outlives both and is
 * `gloss-store.ts`. Then the site, a request of a few hundred bytes behind the
 * edge cache, which is `gloss-request.ts`. This file is which of them is asked
 * and when.
 *
 * **The entry objects are static files, so asking costs the site nothing.**
 * That is the whole reason neither reader carries a dictionary of glosses: a
 * word it has met before needs no request, and a word it has not needs one
 * small one.
 *
 * Two read it. The extension's panel, over the wire to `siteUrl`, and the word
 * segmenter at `/tools/chinese-word-segmenter/`, which is on the site already
 * and passes an empty one.
 */
export interface GlossesProperties {
  /** Where the site is, with no trailing slash. */
  readonly siteUrl: string;

  /** How a request is made. Handed in so a test needs no network. */
  readonly fetch: typeof globalThis.fetch;

  /** Where answers are kept between sittings. It has its own default. */
  readonly store?: GlossStore;
}

/** The 词典 entries behind the words on a page. */
export class Glosses {
  readonly #siteUrl: string;
  readonly #fetch: typeof globalThis.fetch;
  readonly #store: GlossStore;
  readonly #memory = new GlossMemory();

  constructor(properties: GlossesProperties) {
    this.#siteUrl = properties.siteUrl.replace(/\/$/u, "");
    this.#fetch = properties.fetch;
    this.#store = properties.store ?? new GlossStore();
  }

  /** The entry for one word, from wherever this device can answer soonest. */
  of(word: string): Promise<CanonicalEntry | undefined> {
    return this.#memory.asking(word, async () => this.#found(word));
  }

  /** What this session can say about these words with nothing asked for. */
  held(words: readonly string[]): ReadonlyMap<string, CanonicalEntry> {
    return this.#memory.held(words);
  }

  /** Every entry among these words, asked for together. */
  async allOf(
    words: readonly string[],
  ): Promise<ReadonlyMap<string, CanonicalEntry>> {
    await Promise.all(words.map(async (word) => this.of(word)));

    return this.held(words);
  }

  /**
   * The store first where it is young enough, then the site, then the store.
   *
   * The last of those is the failure case. A request that will not complete
   * falls back to whatever was stored however old it is, because a meaning
   * from last week beats no meaning at all, and only a word this device has
   * never been told about is left for the memory to forget.
   */
  async #found(word: string): Promise<CanonicalEntry | undefined> {
    const url = glossUrl(this.#siteUrl, word);
    const stored = await this.#store.held(url);

    if (stored !== undefined && this.#store.fresh(stored)) {
      return this.#memory.recall(word, stored.entry);
    }

    try {
      const entry = await askedSite(this.#fetch, url);

      // Not awaited. The panel is not waiting on a copy going to disk, and
      // `keep` swallows its own failures, so there is nothing here to handle.
      void this.#store.keep(url, entry);

      return this.#memory.recall(word, entry);
    } catch (error) {
      if (stored === undefined) {
        throw error;
      }

      return this.#memory.recall(word, stored.entry);
    }
  }
}
